import React, { FC, useEffect, useState } from 'react';
import {
  Col,
  Form,
  message,
  Row,
  Switch,
  FormInstance,
  Popover,
  Button,
} from 'antd';
import {
  Input,
  InputNumber,
  PriceInput,
  Select,
  useMaxBreakpoint,
} from '@cloudcar-app/cloudcar-components';
import apiService from 'utils/apiService';
import getScreenSpacings from 'utils/getScreenSpacings';
import MessageError from 'utils/message.errors';
import rutFormValidator from 'utils/rutFormValidator';
import stepsInfo from 'utils/stepsInfo';
import vehicleTypeMapper, {
  soapVehicleTypeTranslate,
  VehicleTypes,
} from 'utils/vehicleTypeMapper';
import { trimFormValues } from 'utils/trimFormValues';
import { AxiosError } from 'axios';
import { PurchaseIntentFormValues } from 'apollo/types';
import { QuestionCircleTwoTone } from '@ant-design/icons';
import getSalesDetails, { Ficha } from 'utils/kaufmannSalesIntegrationService';

import styles from './VehicleInformationForm.module.scss';

interface VehicleInformationProps {
  form: FormInstance<PurchaseIntentFormValues>;
  handleChangeRut: (rut: string) => string;
  checkedTagSelection: boolean;
  setCheckedTagSelection: React.Dispatch<React.SetStateAction<boolean>>;
  checkedSoapSelection: boolean;
  setCheckedSoapSelection: React.Dispatch<React.SetStateAction<boolean>>;
  setCheckedVehiclePayTaxes: React.Dispatch<React.SetStateAction<boolean>>;
  checkedVehiclePayTaxes: boolean;
  setCitDataLoading: React.Dispatch<React.SetStateAction<boolean>>;
  showVehicleType: boolean;
  showVehicleYear: boolean;
}

const VehicleInformation: FC<VehicleInformationProps> = (
  props: VehicleInformationProps,
) => {
  const {
    setCitDataLoading,
    form,
    handleChangeRut,
    checkedVehiclePayTaxes,
    checkedSoapSelection,
    setCheckedSoapSelection,
    checkedTagSelection,
    setCheckedTagSelection,
    setCheckedVehiclePayTaxes,
    showVehicleType,
    showVehicleYear,
  } = props;
  const maxScreen = useMaxBreakpoint();
  const screenSpacings = getScreenSpacings(maxScreen);

  const vehicleTypes = [
    { value: 'Auto/Jeep/SW', label: 'Auto/Jeep/SW', price: 9750 },
    { value: 'Camioneta Furgon', label: 'Camioneta Furgon', price: 13800 },
    { value: 'Minibus', label: 'Minibus', price: 40500 },
    { value: 'Bus particular', label: 'Bus particular', price: 60000 },
    {
      value: 'Camión y tractor',
      label: 'Camión y tractor',
      price: 40500,
    },
    { value: 'Remolque', label: 'Remolque', price: 7050 },
  ];

  const getCarDataFromCit = (cit: string) => {
    setCitDataLoading(true);
    const useValuesFromRegister = false;
    if (!useValuesFromRegister) {
      const getCarDataFromCitBody = {
        carCit: cit,
      };
      apiService
        .getCarDataWithCit(getCarDataFromCitBody)
        .then((response) => {
          setCitDataLoading(false);
          const carData = response.response;
          let vehicleType: string | undefined;
          if (
            carData.vehicleType &&
            Array.from(Object.keys(vehicleTypeMapper)).includes(
              carData.vehicleType,
            )
          ) {
            vehicleType = vehicleTypeMapper[carData.vehicleType];
          }
          form.setFieldsValue({
            vehicleType:
              vehicleType || soapVehicleTypeTranslate[VehicleTypes.Car],
          });
        })
        .catch((error: AxiosError) => {
          const errorMessage =
            (error.response?.data as string) || 'Ha ocurrido un error';
          setCitDataLoading(false);
          if (window.navigator.onLine) {
            message.error(errorMessage, 4);
          }
        });
    } else {
      setCitDataLoading(false);
    }
  };

  const validateCit = (cit: string) => {
    console.log(cit);
    if (cit.length !== 17) {
      return false;
    }
    try {
      const citArray = cit.split('-');
      if (citArray.length !== 2) {
        return false;
      }
      if (citArray[1].length !== 1) {
        return false;
      }
      return true;
    } catch (error) {
      console.log(error);
      return false;
    }
  };
  const handleChangeCit = (cit: string) => {
    if (validateCit(cit)) {
      getCarDataFromCit(cit);
    }
    form.setFieldsValue({ cit });
    return cit;
  };
  const [isLoading, setIsLoading] = useState(false);
  const handleCarInformation = (ficha: Ficha) => {
    const { unidadInfo } = ficha;
    const brandMapping: Record<string, string> = {
      MAXUS: 'Maxus',
      'MERCEDES-BENZ': 'M.Benz',
      smart: 'Smart',
    };

    const brand = brandMapping[unidadInfo.descSector] || null;

    form.setFields([
      {
        name: 'brand',
        value: brand,
        touched: true,
        validating: false,
      },
    ]);
  };
  const handleNroFicha = async () => {
    setIsLoading(true);
    const nroFicha = trimFormValues(
      form.getFieldValue('quotationId') as string,
    );
    if (!nroFicha) {
      console.log('No nroFicha');
      setIsLoading(false);
      return;
    }
    try {
      const salesDetails = await getSalesDetails(nroFicha);
      handleCarInformation(salesDetails);
      const clienteRut = salesDetails.clienteInfo.IdentificadorFiscal;
      const clienteNombre = salesDetails.clienteInfo.NombresRazonSocialCuenta;
      const clienteApellido = salesDetails.clienteInfo.ApellidosCuenta;
      const unidadPatente = salesDetails.unidadInfo.patente;
      const clienteMail = salesDetails.clienteInfo.Mail;

      form.setFields([
        {
          name: 'rut',
          value: clienteRut,
          touched: true,
          validating: false,
        },
        {
          name: 'insuranceFirstName',
          value: clienteNombre,
          touched: true,
          validating: false,
        },
        {
          name: 'insuranceLastName',
          value: clienteApellido,
          touched: true,
          validating: false,
        },
        {
          name: 'carPlate',
          value: unidadPatente,
          touched: true,
          validating: false,
        },
        {
          name: 'email',
          value: clienteMail,
          touched: true,
          validating: false,
        },
      ]);
    } catch (error) {
      console.log(`Error fetching sales details:`, error);
    } finally {
      setIsLoading(false);
    }
  };
  useEffect(() => {
    const values = form.getFieldsValue();
    console.log('Current form values:', values);
  }, [form]);

  return (
    <Row
      gutter={[screenSpacings.L, screenSpacings.L]}
      justify="start"
      align="middle"
      style={{ width: '100%' }}
    >
      <Col xs={24}>
        <h3> Datos para la tramitación</h3>
      </Col>
      <Col xs={24}>
        <Row style={{ display: 'flex', flexDirection: 'row' }} gutter={24}>
          <Col>
            <Row
              gutter={12}
              className={styles.FinancingSwitch}
              style={{ display: 'flex', flexDirection: 'row' }}
            >
              <Col>
                <Switch
                  checked={checkedVehiclePayTaxes}
                  onChange={() =>
                    setCheckedVehiclePayTaxes(!checkedVehiclePayTaxes)
                  }
                />
              </Col>
              <Col>
                <p>Paga impuestos</p>
              </Col>
            </Row>
          </Col>
          <Col>
            <Row
              gutter={12}
              className={styles.FinancingSwitch}
              style={{ display: 'flex', flexDirection: 'row' }}
            >
              <Col>
                <Switch
                  checked={checkedSoapSelection}
                  onChange={() =>
                    setCheckedSoapSelection(!checkedSoapSelection)
                  }
                />
              </Col>
              <Col>
                <p>Seguro obligatorio</p>
              </Col>
            </Row>
          </Col>
          <Col>
            <Row
              gutter={12}
              className={styles.FinancingSwitch}
              style={{ display: 'flex', flexDirection: 'row' }}
            >
              <Col>
                <Switch
                  checked={checkedTagSelection}
                  onChange={() => setCheckedTagSelection(!checkedTagSelection)}
                />
              </Col>
              <Col>
                <p>Tag</p>
              </Col>
            </Row>
          </Col>
        </Row>
      </Col>

      <Col xs={24} sm={8}>
        <Form.Item name="quotationId" className={styles.FormInput}>
          <Input
            label="Número de ficha de venta del vehículo"
            placeholder="N° ficha de venta del vehículo"
            compact
            className={styles.Input}
            suffix={
              <Button
                type="text"
                icon={<QuestionCircleTwoTone />}
                onClick={handleNroFicha}
                loading={isLoading}
              >
                Buscar
              </Button>
            }
          />
        </Form.Item>
      </Col>
      <Col xs={24} sm={8}>
        <Form.Item
          name="rut"
          className={styles.FormInput}
          rules={[
            {
              required: true,
              message: MessageError.emptyFieldsMessages.rut,
            },
            {
              whitespace: true,
              message: MessageError.emptyFieldsMessages.rut,
            },
            {
              validator: rutFormValidator,
            },
          ]}
        >
          <Input
            label="Rut cliente"
            placeholder="Rut cliente"
            onChange={(e) => handleChangeRut(e.target.value)}
            required
            compact
            className={styles.Input}
          />
        </Form.Item>
      </Col>

      {checkedVehiclePayTaxes &&
        stepsInfo.find((step) => step.route === '/procedureSelection') && (
          <Col xs={24} sm={8}>
            <Form.Item name="cit" className={styles.FormInput}>
              <Input
                label="CIT"
                placeholder="CIT (Código de informe técnico)"
                required
                compact
                className={styles.Input}
                onChange={(event) => handleChangeCit(event.target.value)}
              />
            </Form.Item>
          </Col>
        )}

      {showVehicleType && (
        <Col xs={24} sm={8}>
          <Form.Item
            name="vehicleType"
            className={styles.FormInput}
            rules={[
              {
                required: true,
                message: MessageError.emptyFieldsMessages.vehicleType,
              },
            ]}
          >
            <Select
              compact
              label="Tipo de vehículo"
              placeholder="Tipo de vehículo"
              options={vehicleTypes.map((option) => ({
                label: option.label,
                value: option.value,
              }))}
            />
          </Form.Item>
        </Col>
      )}
      {showVehicleYear && (
        <Col xs={24} sm={8}>
          <Form.Item
            name="year"
            className={styles.FormInput}
            rules={[
              {
                required: true,
                message: MessageError.emptyFieldsMessages.year,
              },
              {
                type: 'number',
                message: MessageError.invalidFormatFieldsMessages.year,
              },
            ]}
          >
            <InputNumber
              onKeyPress={(event) => {
                if (!/[0-9]/.test(event.key)) {
                  event.preventDefault();
                }
              }}
              label="Año"
              placeholder="Año"
              required
              compact
              className={styles.Input}
            />
          </Form.Item>
        </Col>
      )}

      <Col xs={24} sm={8}>
        <Form.Item
          name="price"
          className={styles.FormInput}
          rules={[
            {
              required: true,
              message: MessageError.emptyFieldsMessages.price,
            },
            {
              type: 'number',
              message: MessageError.invalidFormatFieldsMessages.price,
            },
          ]}
        >
          <PriceInput placeholder="Valor neto factura" />
        </Form.Item>
      </Col>
    </Row>
  );
};
export default VehicleInformation;
